import React, { createContext, useContext, useState, useCallback } from 'react'

type ToastType = 'default' | 'success' | 'error' | 'warning'
type ToastVariant = 'default' | 'destructive'

export interface Toast {
  id: string
  title?: string
  description?: string
  /**
   * Compatibility with shadcn-style toasts used across the codebase.
   * `variant: "destructive"` maps to `type: "error"`.
   */
  variant?: ToastVariant
  type?: ToastType
  duration?: number
}

interface ToastContextType {
  toast: (toast: Omit<Toast, 'id'>) => void
  dismiss: (id: string) => void
  toasts: Toast[]
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const toast = useCallback(({ title, description, variant, type = 'default', duration = 5000 }: Omit<Toast, 'id'>) => {
    const resolvedType: ToastType =
      variant === 'destructive' ? 'error' : type

    const id = Math.random().toString(36).substring(2, 9)
    const newToast = { id, title, description, variant, type: resolvedType, duration }
    
    setToasts((prev) => [...prev, newToast])

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
      }, duration)
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toast, dismiss, toasts }}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
